import { CheckBox, Spacer, VDBText } from "@components/atoms";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { SearchResultTabletHeaderProps, SortingOptions } from "./types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useSortedTabletListData } from "../../../hooks/useSortCustomData";
import { useTranslation } from "react-i18next";
import { setColumnWidths, useUserInfo } from "@redux/slices/userInfoSlice";
import { VDBIcons } from "@utils/VDBIcons";
import { calculateEmptyWidth, getKeysToRemove } from "./utils";
import { getProductListViewData } from "@scenes/searchResults/Utils";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, { useAnimatedStyle } from "react-native-reanimated";
import * as styledDiv from "styled-components";
import { useHorizontalOffset } from "@scenes/searchResults/components/HorizontalScrollContext";
import { getProductThemeFromType } from "@components/common/Header/utils";
import { Platform, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useHorizontalScrollMultiplier } from "@scenes/searchResults/components/HorizontalScrollMultiplierContext";
import { TRAILING_SPACER_WIDTH } from "@scenes/searchResults/components/HorizontalScrollWrapper";
import useResetFilter from "../../../hooks/useResetFilter";
import { getRequestType, RequestTypes } from "@scenes/requests/utils/utils";

const TabletListHeader = ({
  checkBoxSelection,
  ismItemChecked,
  listMargin,
  productType,
  onSortChange,
  showCheckbox,
  showFavorite = true,
  fromBulkList = false,
  customizeColumnParams,
  fromRequestDetail = false,
  request_type,
  hasAnyOfferDetails,
  isTotalOfferDiscountApplied,
  viewType = "",
  showListPrice,
  isTotalOfferPriceApply,
  sectionName = "Single Stones",
  selectedStoneType,
  stockItemRequest,
  useMobileWebView = false,
}: SearchResultTabletHeaderProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const dispatch = useDispatch();

  // Get horizontal offset from context for transform-based scrolling (large screen only)
  const xOffset = useHorizontalOffset();
  const horizontalAnimatedStyle = useAnimatedStyle(() => ({
    // Only apply transform on large screen; tablet uses native ScrollView
    transform: [
      { translateX: theme.isLargeScreen && xOffset ? -xOffset.value : 0 },
    ],
  }));
  const userPriceMode: number = userInfo.user.price_mode;
  const columnWidths = useSelector((state) => state.userInfo.columnWidths);
  const horizontalMultiplier = useHorizontalScrollMultiplier();
  const productListViewData = getProductListViewData(productType);
  const baseWidthsRef = useRef<{ [key: string]: number }>({});
  const liveWidthRef = useRef<{ [key: string]: number }>({});
  const [columnWidth, setColumnWidth] = useState({});
  const defaultListDataOptions = useSortedTabletListData(
    productType,
    userPriceMode,
    sectionName,
    fromBulkList,
    fromRequestDetail,
    selectedStoneType
  );
  const productTheme = getProductThemeFromType(productType);

  const listDataOptions =
    customizeColumnParams && customizeColumnParams.length > 0
      ? customizeColumnParams
      : productListViewData && productListViewData.length > 0
      ? productListViewData
      : defaultListDataOptions;

  const keysToRemove = getKeysToRemove({
    request_type,
    hasAnyOfferDetails,
    viewType,
    showListPrice,
    isTotalOfferDiscountApplied,
    isTotalOfferPriceApply,
    sectionName,
    stockItemRequest,
  });

  const listDataOptionsWithListPrice = listDataOptions
    .filter((item: any) => !keysToRemove.includes(item.response_key))
    .map((item: any) => {
      let updatedLabel = item.label;
      if (fromRequestDetail) {
        if (getRequestType(viewType) === RequestTypes.Quote) {
          if (request_type !== "buyer_view") {
            switch (item.response_key) {
              case "offer_total_sales_price":
                updatedLabel = t(
                  "searchResult.listViewTablet.header.totalPrice"
                );
                break;
              case "total_sales_price":
                updatedLabel = t(
                  "searchResult.listViewTablet.header.totalPrice"
                );
                break;

              case "offer_discount_percent":
                updatedLabel = t(
                  "searchResult.listViewTablet.header.discountPercent"
                );
                break;
              case "offer_price_per_carat":
                updatedLabel = t("searchResult.listViewTablet.header.pct");
                break;
              case "price_per_carat":
                updatedLabel = t("searchResult.listViewTablet.header.pct");
                break;

              default:
                break;
            }
          } else {
            if (hasAnyOfferDetails) {
              switch (item.response_key) {
                case "discount_percent":
                  updatedLabel = t(
                    "searchResult.listViewTablet.header.listedDiscountPercent"
                  );
                  break;
              }
            } else {
              switch (item.response_key) {
                case "total_sales_price":
                  updatedLabel = t(
                    "searchResult.listViewTablet.header.totalPrice"
                  );
                  break;
                case "price_per_carat":
                  updatedLabel = t("searchResult.listViewTablet.header.pct");
                  break;
              }
            }
          }
        } else {
          if (hasAnyOfferDetails) {
            switch (item.response_key) {
              case "discount_percent":
                updatedLabel = t(
                  "searchResult.listViewTablet.header.listedDiscountPercent"
                );
                break;
            }
          } else {
            switch (item.response_key) {
              case "total_sales_price":
                updatedLabel = t(
                  "searchResult.listViewTablet.header.totalPrice"
                );
                break;
              case "price_per_carat":
                updatedLabel = t("searchResult.listViewTablet.header.pct");
                break;
            }
          }
        }
      }
      return {
        ...item,
        label: updatedLabel,
      };
    });

  const [sortType, setSortType] = useState<{
    [label: string]: SortingOptions;
  }>({});

  useResetFilter(() => {
    setSortType({});
  });

  const handleSortPress = useCallback(
    (item: any) => {
      if (!item.is_sortable) return;
      let newSortingOption: SortingOptions;

      // If we haven't sorted on this label yet, check the default or fall back
      if (!sortType[item.label]) {
        newSortingOption =
          item.default_sort === SortingOptions.ASCENDING_SORT
            ? SortingOptions.ASCENDING_SORT
            : SortingOptions.DESCENDING_SORT;
      } else {
        newSortingOption =
          sortType[item.label] === SortingOptions.ASCENDING_SORT
            ? SortingOptions.DESCENDING_SORT
            : SortingOptions.ASCENDING_SORT;
      }

      setSortType({ [item.label]: newSortingOption });

      if (onSortChange) {
        onSortChange({
          classic_sort: item?.sort_key || item.response_key,
          classic_sort_order: newSortingOption,
        });
      }
    },
    [sortType, onSortChange]
  );

  const getSortingIcon = useCallback((option?: SortingOptions) => {
    if (option === SortingOptions.ASCENDING_SORT) {
      return VDBIcons.SORT_ASCENDING;
    } else if (option === SortingOptions.DESCENDING_SORT) {
      return VDBIcons.SORT_DESCENDING;
    }
    return VDBIcons.TABLE_SORT;
  }, []);

  const storageKey = `${productType}_${sectionName}`;

  const handleGestureUpdate = useCallback(
    (translationX: number, columnKey: string) => {
      const baseWidth =
        baseWidthsRef.current[columnKey] ??
        columnWidth?.[storageKey]?.[userPriceMode]?.[columnKey] ??
        100;

      let newWidth = baseWidth + translationX;
      newWidth = Math.max(30, newWidth);
      setColumnWidth((prev) => ({
        ...prev,
        [columnKey]: Math.floor(newWidth),
      }));
      liveWidthRef.current[columnKey] = newWidth;
    },
    [columnWidth, storageKey, userPriceMode]
  );

  const handleGestureEnd = useCallback(
    (columnKey: string, defaultWidth: string) => {
      const width = liveWidthRef.current[columnKey];
      dispatch(
        setColumnWidths({
          productType: storageKey,
          priceMode: userPriceMode,
          key: columnKey,
          width,
        })
      );
      baseWidthsRef.current[columnKey] =
        liveWidthRef.current[columnKey] ??
        columnWidth?.[storageKey]?.[userPriceMode]?.[columnKey] ??
        defaultWidth;
    },
    [columnWidth, storageKey, userPriceMode, dispatch]
  );

  const createPanGesture = useCallback(
    (columnKey: string, defaultWidth: string) => {
      return Gesture.Pan()
        .runOnJS(true)
        .activeOffsetX([-5, 5])
        .failOffsetY([-10, 10])
        .onUpdate((event) => {
          handleGestureUpdate(event.translationX, columnKey);
        })
        .onEnd(() => {
          handleGestureEnd(columnKey, defaultWidth);
        });
    },
    [handleGestureUpdate, handleGestureEnd]
  );

  useEffect(() => {
    setColumnWidth(columnWidths);
  }, [columnWidths]);

  const displayImage =
    !theme.isLargeScreen && !theme.isTablet && !useMobileWebView;

  return (
    <Animated.View style={[{ flexDirection: "row" }, horizontalAnimatedStyle]}>
      <Overlay theme={theme} listMargin={listMargin}>
        <FirstContentMapContainer>
          {theme.isTablet && showCheckbox && (
            <TableCell>
              <CheckBox
                fillColor={theme["knob-bor"]}
                checkBoxSize={20}
                checkedFunction={checkBoxSelection}
                isChecked={ismItemChecked}
              />
            </TableCell>
          )}
          {!userInfo.isIFrame() && (
            <SpaceBox
              showCheckbox={showCheckbox}
              showFavorite={showFavorite}
              multiplier={horizontalMultiplier}
              useMobileWebView={useMobileWebView}
            />
          )}
          {listDataOptionsWithListPrice.map((item, index) => {
            if (!(item?.is_visible === undefined ? true : item.is_visible))
              return null;
            const requestDetailsCols = item?.deafult_width?.split("px")[0];
            const rawColWidth =
              columnWidth?.[item?.name] ||
              columnWidth?.[storageKey]?.[userPriceMode]?.[item.name] ||
              item?.deafult_width?.split("px")[0];
            const colWidth = Number(rawColWidth) * horizontalMultiplier;
            const maxWidth =
              Number(item?.deafult_width?.split("px")[0]) *
              horizontalMultiplier;
            const requestDetailsWidth =
              Number(requestDetailsCols) * horizontalMultiplier;
            return (
              <React.Fragment key={index}>
                <StyledDiv
                  theme={theme}
                  productsType={productTheme}
                  options={sortType[item.label]}
                >
                  <InfoContainer
                    onPress={() => handleSortPress(item)}
                    disabled={item?.is_sortable === false}
                    containerWidth={item?.new_default_width}
                    minWidth={colWidth}
                    displayImage={displayImage}
                    is_sortable={item?.is_sortable}
                    alignment={item?.alignment || "flex-start"}
                    maxWidth={maxWidth}
                    width={fromRequestDetail ? requestDetailsWidth : colWidth}
                  >
                    <View style={{ zIndex: 1, overflow: "hidden" }}>
                      <VDBText
                        theme={theme}
                        color={theme["core-06"]}
                        type={typography["Minor-R"]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {t(`${item?.label}`)}
                      </VDBText>
                    </View>

                    {item.is_sortable ? (
                      <VDBIcon
                        color={theme["option-cont"]}
                        size={SpaceType.LargeDouble}
                        variant={IconsVariant.VDBCustom}
                        name={getSortingIcon(sortType[item.label])}
                      />
                    ) : (
                      <Spacer size={SpaceType.LargeDouble} />
                    )}
                  </InfoContainer>
                </StyledDiv>

                {(theme.isLargeScreen ||
                  theme.isTablet ||
                  useMobileWebView) && (
                  <GestureDetector
                    gesture={createPanGesture(
                      item.name,
                      item?.deafult_width?.split("px")[0]
                    )}
                  >
                    <ResizerHandle />
                  </GestureDetector>
                )}
              </React.Fragment>
            );
          })}
          <TrailingSpacer />
        </FirstContentMapContainer>
      </Overlay>
    </Animated.View>
  );
};

const SpaceBox = styled.View<{
  showFavorite: boolean;
  showCheckbox: boolean;
  useMobileWebView: boolean;
  multiplier: number;
}>`
  width: ${({
    theme,
    showFavorite,
    showCheckbox,
    useMobileWebView,
    multiplier,
  }) =>
    `${
      calculateEmptyWidth({
        isTablet: theme.isTablet,
        showCheckbox,
        showFavorite,
        useMobileWebView,
      }) * multiplier
    }px`};
`;

const FirstContentMapContainer = styled.View`
  flex-direction: row;
  ${({ theme }) => !theme.isLargeScreen && "align-items: center;"}
`;
const TableCell = styled.View`
  padding: ${SpaceType.Double}px;
  flex-direction: row;
  align-items: center;
`;

const ResizerHandle = styled(Animated.View)`
  width: 3px;
  background-color: ${({ theme }) => theme["core-02"]};
  cursor: col-resize;
`;

const TrailingSpacer = styled.View`
  width: ${TRAILING_SPACER_WIDTH}px;
`;

const InfoContainer = styled.TouchableOpacity<{
  containerWidth?: string;
  displayImage?: boolean;
  alignment?: string;
  is_sortable?: boolean;
  maxWidth?: string;
  minWidth?: string;
  width?: any;
}>`
  padding-top: ${({ displayImage }) =>
    displayImage ? `${SpaceType.Big}px` : "5px"};
  padding-bottom: ${({ displayImage }) =>
    displayImage ? `${SpaceType.Big}px` : "5px"};
  padding-left: ${SpaceType.Basic}px;
  padding-right: 6px;
  display: flex;
  overflow: hidden;
  width: ${({ width }) => (width ? `${width}px` : "100%")};
  justify-content: ${({ is_sortable, alignment }) =>
    is_sortable ? "space-between" : alignment};
  flex-direction: row;
  align-items: center;
  justify-content: ${({ is_sortable, alignment }) =>
    is_sortable ? "space-between" : alignment};
  min-width: ${({ minWidth }) => (minWidth ? `${minWidth}px` : "auto")};
  max-width: ${({ containerWidth }) =>
    containerWidth ? `${containerWidth}px` : "auto"};
`;
const Overlay = styled.Pressable<any>`
  flex-direction: column;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background-color: ${({ theme }) => theme["core-03"]};
`;

const StyledDiv =
  Platform.OS === "web"
    ? styledDiv.default.div<{
        productsType: string;
        options: string;
      }>`
      background-color: ${({ options, productsType, theme }) =>
        options === SortingOptions.DESCENDING_SORT
          ? theme?.[`${productsType}-01`]
          : options === SortingOptions.ASCENDING_SORT
          ? theme?.["core-06"]
          : theme?.["core-03"]};
      div {
        color: ${({ options, theme, productsType }) =>
          options === SortingOptions.ASCENDING_SORT ||
          options === SortingOptions.DESCENDING_SORT
            ? theme[`${productsType}-05`] + " !important"
            : theme["core-06"]};
      }
      &:hover {
        background-color: ${({ productsType, theme }) =>
          theme[`${productsType}-01`]};
        
        div {
          color: ${({ theme, productsType }) =>
            theme[`${productsType}-05`] + " !important"};
        }
      }
    `
    : styled.View<{
        productsType: string;
        options: string;
      }>`
        background-color: ${({ options, productsType, theme }) =>
          Platform.OS === "web"
            ? options === SortingOptions.DESCENDING_SORT
              ? theme?.[`${productsType}-01`]
              : options === SortingOptions.ASCENDING_SORT
              ? theme?.["core-06"]
              : theme?.["core-03"]
            : "transparent"};
        div {
          color: ${({ options, theme, productsType }) =>
            options === SortingOptions.ASCENDING_SORT ||
            options === SortingOptions.DESCENDING_SORT
              ? theme[`${productsType}-05`] + " !important"
              : theme["core-06"]};
        }
      `;

export default memo(TabletListHeader, (prevProps, nextProps) => {
  return (
    prevProps.ismItemChecked === nextProps.ismItemChecked &&
    prevProps.listMargin === nextProps.listMargin &&
    prevProps.productType === nextProps.productType &&
    prevProps.sectionName === nextProps.sectionName &&
    prevProps.checkBoxSelection === nextProps.checkBoxSelection &&
    prevProps.hasAnyOfferDetails === nextProps.hasAnyOfferDetails &&
    prevProps.stockItemRequest === nextProps.stockItemRequest
  );
});
